import React, { useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';

import { Collapsible } from '@techstyle/react-components';

import {
  trackProductSetSummaryClose,
  trackProductSetSummaryOpen,
} from '../../actions/products';
import { mobile } from '../../styles';
import { getAvailableQuantity } from '../../utils/productQuantity';
import { isPreorderable } from '../../utils/selectors';
import { isOneSize } from '../../utils/sizes';
import ProductSetItemSummary from '../ProductSetItemSummary/ProductSetItemSummary';
import { Component as StaticIcon } from '../StaticIcon';

const SetItemSummaryCollapsible = styled(Collapsible)``;

const CollapsibleContent = styled(Collapsible.Content)`
  border: none;
  margin-left: -92px;

  ${mobile`
    margin-left: -126px;
  `};

  &[aria-hidden='true'] {
    border: none;
  }
`;

const CollapsibleContentWrapper = styled.div`
  padding: ${props => props.theme.spacing.tiny}px 0;

  ${mobile`
  padding: 17px 0;
  `}
`;

const CollapsibleButton = styled(Collapsible.Button)`
  ${props => props.theme.collapsibleTrigger.defaultStyle};
  font-size: 10px;
  text-align: center;
  font-weight: normal;
  line-height: 1.4;
  padding: 0;
  ${mobile`
    padding: 0 0 ${props => props.theme.spacing.moderate}px 0;
  `};

  ${props =>
    props.isOpen &&
    css`
      padding: 0 0 ${props => props.theme.spacing.tiny}px 0;
    `}

  &:focus {
    outline: none;
  }
`;

const DownStaticIcon = styled(StaticIcon).attrs({
  type: 'caret',
  size: 8,
})`
  margin-left: auto;
  color: ${props => props.theme.colors.default};
`;

const UpStaticIcon = styled(DownStaticIcon)`
  transform: rotate(180deg);
`;

const SetItemDetail = styled.div`
  flex: 1;
  width: 100%;
`;

function ToggleButton({ label, onClick, isOpen, ...rest }) {
  return (
    <CollapsibleButton isOpen={isOpen} onClick={onClick} {...rest}>
      {label}
      {isOpen ? <UpStaticIcon /> : <DownStaticIcon />}
    </CollapsibleButton>
  );
}

ToggleButton.propTypes = {
  label: PropTypes.node,
  onClick: PropTypes.func,
  isOpen: PropTypes.bool,
};

const ProductSetDetailOptionSummary = ({
  children,
  currentSelection,
  isSoldOut,
  setItem,
  setIndex,
}) => {
  const dispatch = useDispatch();
  const onOpening = () => {
    dispatch(trackProductSetSummaryOpen());
  };

  const onClosing = () => {
    dispatch(trackProductSetSummaryClose());
  };

  const isPreorderItem = useSelector(state =>
    isPreorderable(state, { product: setItem })
  );

  const availableQuantity = isSoldOut
    ? 0
    : getAvailableQuantity(
        currentSelection.size.sizeProduct,
        setItem,
        isPreorderItem
      );

  const defaultIsOpen =
    !currentSelection.size.sizeProduct || availableQuantity === 0;
  const [isOpen, setIsOpen] = useState(defaultIsOpen);

  const handleCollapsibleToggle = () => {
    setIsOpen(!isOpen);
  };

  const memoizedIsOneSize = useMemo(() => isOneSize(setItem), [setItem]);

  const label = (
    <ProductSetItemSummary
      isOneSize={memoizedIsOneSize}
      currentSelection={currentSelection}
    />
  );

  return (
    <SetItemSummaryCollapsible
      data-testid="set-summary"
      isOpen={isOpen}
      onOpen={onOpening}
      onClose={onClosing}
    >
      <ToggleButton
        label={label}
        onClick={handleCollapsibleToggle}
        isOpen={isOpen}
        data-autotag={`pdpSetExpandSizeBtn${setIndex}`}
      />
      <CollapsibleContent>
        <CollapsibleContentWrapper>
          <SetItemDetail>{children}</SetItemDetail>
        </CollapsibleContentWrapper>
      </CollapsibleContent>
    </SetItemSummaryCollapsible>
  );
};

ProductSetDetailOptionSummary.propTypes = {
  children: PropTypes.node.isRequired,
  currentSelection: PropTypes.object,
  isSoldOut: PropTypes.bool,
  setItem: PropTypes.object,
  setIndex: PropTypes.number,
};

export default ProductSetDetailOptionSummary;
